import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiUpload, FiCheck } from 'react-icons/fi';
import { mediaAPI } from '../../api/services';
import { formatNumber, parseNumber } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';

/**
 * Reusable CRUD table component for admin pages.
 *
 * Props:
 * - title: string
 * - columns: Array<{ key, label, render? }>
 * - fetchData: () => Promise<Array>
 * - onAdd: (formData) => Promise
 * - onUpdate: (id, formData) => Promise
 * - onDelete: (id) => Promise
 * - formFields: Array<{ name, label, type?, options?, placeholder? }>
 *     type can be: 'text' | 'number' | 'textarea' | 'select' | 'datetime-local' | 'file'
 *     file type triggers media upload and stores the resulting UUID
 * - entityName: string
 * - customActions?: (item) => ReactNode
 * - onAddSuccess?: (response) => void
 */
const CrudTable = ({ title, columns, fetchData, onAdd, onUpdate, onDelete, formFields, entityName, customActions, onAddSuccess }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [uploadingFields, setUploadingFields] = useState({});
  const { t } = useTranslation();

  const loadData = async () => {
    setLoading(true);
    try {
      const items = await fetchData();
      setData(items);
    } catch {
      showToast(t('common.error_loading'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openAdd = () => {
    setEditItem(null);
    const emptyForm = {};
    const fields = typeof formFields === 'function' ? formFields({}) : formFields;
    fields.forEach((f) => {
      emptyForm[f.name] = f.type === 'multiselect' ? (f.defaultValue ?? []) : (f.defaultValue ?? '');
    });
    setForm(emptyForm);
    setUploadingFields({});
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    const filledForm = {};
    const fields = typeof formFields === 'function' ? formFields(item) : formFields;
    fields.forEach((f) => {
      if (f.type === 'multiselect') {
        filledForm[f.name] = item[f.name] ?? [];
      } else {
        filledForm[f.name] = item[f.name] ?? '';
      }
    });
    setForm(filledForm);
    setUploadingFields({});
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, dataset } = e.target;
    
    if (dataset.type === 'number') {
      // Format with spaces as user types
      const formatted = formatNumber(value);
      setForm((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  /** Handle file upload for 'file' type fields */
  const handleFileUpload = async (fieldName, file) => {
    if (!file) return;

    setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
    try {
      const res = await mediaAPI.upload(file, 'IMAGE');
      const mediaId = res.data?.data?.id;
      if (mediaId) {
        setForm((prev) => ({ ...prev, [fieldName]: mediaId }));
        showToast(t('common.image_uploaded'));
      } else {
        showToast(t('common.upload_failed'), 'error');
      }
    } catch (err) {
      showToast(t('common.upload_failed'), 'error');
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      const fields = typeof formFields === 'function' ? formFields(form) : formFields;
      // Convert numeric fields and clean empty strings
      fields.forEach((f) => {
        if (f.type === 'number' && payload[f.name]) {
          payload[f.name] = parseNumber(payload[f.name]);
        }
        // Remove empty optional fields so backend doesn't get ""
        if (payload[f.name] === '' || payload[f.name] === undefined) {
          delete payload[f.name];
        }
      });

      if (editItem) {
        await onUpdate(editItem.id, payload);
        showToast(t('common.updated_success', { entity: entityName }));
      } else {
        const res = await onAdd(payload);
        showToast(t('common.added_success', { entity: entityName }));
        if (onAddSuccess) {
          onAddSuccess(res);
        }
      }
      setShowModal(false);
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || t('common.operation_failed');
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirm_delete', { entity: entityName.toLowerCase() }))) return;
    try {
      await onDelete(id);
      showToast(t('common.deleted_success', { entity: entityName }));
      await loadData();
    } catch {
      showToast(t('common.delete_failed'), 'error');
    }
  };

  const filtered = data.filter((item) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return columns.some((col) => {
      const val = item[col.key];
      return val && String(val).toLowerCase().includes(s);
    });
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>{title}</h1>
        <button className="btn btn-primary btn-sm" onClick={openAdd}>
          <FiPlus /> {t('common.add_entity', { entity: entityName })}
        </button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20, maxWidth: 360 }}>
        <FiSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input
          className="form-input"
          style={{ paddingLeft: 40, width: '100%' }}
          placeholder={t('common.search_placeholder', { title: title.toLowerCase() })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📋</div>
          <h3>{t('common.no_data_found', { title: title.toLowerCase() })}</h3>
          <p>{search ? t('common.try_another_search') : t('common.add_your_first', { entity: entityName.toLowerCase() })}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                {columns.map((col) => (
                  <th key={col.key} style={thStyle}>{col.label}</th>
                ))}
                <th style={{ ...thStyle, textAlign: 'right' }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => (
                <tr key={item.id} style={trStyle}>
                  <td style={tdStyle}>{idx + 1}</td>
                  {columns.map((col) => (
                    <td key={col.key} style={tdStyle}>
                      {col.render ? col.render(item) : (item[col.key] ?? '—')}
                    </td>
                  ))}
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {customActions && customActions(item)}
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(item)} title={t('common.edit')}>
                        <FiEdit2 />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)} title={t('common.delete')}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={overlayStyle} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                {editItem ? t('common.edit_entity', { entity: entityName }) : t('common.add_entity', { entity: entityName })}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem' }}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {(typeof formFields === 'function' ? formFields(form) : formFields).map((field) => (
                <div className="form-group" key={field.name}>
                  <label htmlFor={`modal-${field.name}`}>{field.label}</label>

                  {field.type === 'file' ? (
                    <FileUploadField
                      fieldName={field.name}
                      currentValue={form[field.name]}
                      uploading={uploadingFields[field.name]}
                      onUpload={handleFileUpload}
                    />
                  ) : field.type === 'textarea' ? (
                    <textarea
                      id={`modal-${field.name}`}
                      className="form-input"
                      name={field.name}
                      value={form[field.name] || ''}
                      onChange={handleChange}
                      placeholder={field.placeholder || ''}
                      rows={4}
                      disabled={field.disabled}
                    />
                  ) : field.type === 'multiselect' ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(field.options || []).map((opt) => {
                        const selected = Array.isArray(form[field.name]) && form[field.name].includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              const current = Array.isArray(form[field.name]) ? form[field.name] : [];
                              const next = selected
                                ? current.filter(v => v !== opt.value)
                                : [...current, opt.value];
                              setForm(prev => ({ ...prev, [field.name]: next }));
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 8,
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              border: selected ? '2px solid var(--accent)' : '1px solid var(--border)',
                              background: selected ? 'var(--accent-bg)' : 'var(--bg-input, #f8f9fa)',
                              color: selected ? 'var(--accent)' : 'var(--text-secondary)',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : field.type === 'select' ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select
                        id={`modal-${field.name}`}
                        className="form-input"
                        name={field.name}
                        value={form[field.name] || ''}
                        onChange={handleChange}
                        style={{ flex: 1 }}
                        disabled={field.disabled}
                      >
                        <option value="">{t('common.select')}</option>
                        {(field.options || []).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {field.onQuickAdd && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ padding: '0 12px', border: '1px solid var(--border)' }}
                          onClick={field.onQuickAdd}
                          title={`Add new ${field.label.toLowerCase()}`}
                        >
                          <FiPlus />
                        </button>
                      )}
                    </div>
                  ) : field.type === 'number' ? (
                    <input
                      id={`modal-${field.name}`}
                      className="form-input"
                      type="text"
                      name={field.name}
                      data-type="number"
                      value={form[field.name] || ''}
                      onChange={handleChange}
                      placeholder={field.placeholder || ''}
                      disabled={field.disabled}
                    />
                  ) : (
                    <input
                      id={`modal-${field.name}`}
                      className="form-input"
                      type={field.type || 'text'}
                      name={field.name}
                      value={form[field.name] || ''}
                      onChange={handleChange}
                      placeholder={field.placeholder || ''}
                      disabled={field.disabled}
                    />
                  )}
                </div>
              ))}

              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={saving}>
                {saving ? t('common.saving') : editItem ? t('common.save') : t('common.create')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className={`toast ${toast.type}`}>{toast.message}</div>}
    </div>
  );
};

/** File upload sub-component for media fields */
const FileUploadField = ({ fieldName, currentValue, uploading, onUpload }) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const { t } = useTranslation();

  const handleClick = () => fileInputRef.current?.click();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    // Upload
    onUpload(fieldName, file);
  };

  return (
    <div>
      <div
        onClick={handleClick}
        style={{
          border: '2px dashed var(--border)',
          borderRadius: 'var(--radius)',
          padding: '20px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          background: currentValue ? 'var(--accent-bg)' : 'var(--bg-input)',
          transition: 'var(--transition)',
        }}
      >
        {uploading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--accent)' }}>
            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
            <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{t('common.uploading')}</span>
          </div>
        ) : currentValue ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--accent)' }}>
            <FiCheck />
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{t('common.image_uploaded')}</span>
          </div>
        ) : (
          <div style={{ color: 'var(--text-muted)' }}>
            <FiUpload style={{ fontSize: '1.3rem', marginBottom: 6 }} />
            <p style={{ fontSize: '0.85rem', fontWeight: 500 }}>{t('common.click_to_upload')}</p>
            <p style={{ fontSize: '0.75rem', marginTop: 4 }}>JPG, PNG, WebP or GIF</p>
          </div>
        )}
      </div>

      {preview && (
        <img
          src={preview}
          alt="Preview"
          style={{
            width: '100%',
            height: 120,
            objectFit: 'cover',
            borderRadius: 'var(--radius)',
            marginTop: 10,
          }}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: '0.78rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-muted)',
  borderBottom: '1px solid var(--border)',
};

const tdStyle = {
  padding: '14px 16px',
  fontSize: '0.9rem',
  borderBottom: '1px solid var(--border)',
};

const trStyle = {
  transition: 'background 0.15s',
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: 20,
};

const modalStyle = {
  background: '#fff',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-xl)',
  padding: 32,
  width: '100%',
  maxWidth: 480,
  maxHeight: '90vh',
  overflowY: 'auto',
};

export default CrudTable;
